/** Same-origin API helper (Better Auth session cookie is sent automatically). */
export async function api(path: string, opts: RequestInit = {}): Promise<Response> {
  return fetch("/api" + path, {
    ...opts,
    headers: { "content-type": "application/json", ...(opts.headers ?? {}) },
  });
}

export async function apiJson<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const res = await api(path, opts);
  if (!res.ok) {
    // Surface the server's error detail (onError returns { error, detail }).
    let detail = "";
    try {
      const body = (await res.json()) as { error?: string; detail?: string };
      detail = body.detail || body.error || "";
    } catch {
      /* non-JSON body */
    }
    throw new Error(`${path} → ${res.status}${detail ? `: ${detail}` : ""}`);
  }
  return (await res.json()) as T;
}

// ── Shared shapes (mirror contracts/schemas.ts; kept minimal for the client) ──
export interface DbMessage { id: string; role: "user" | "assistant"; content: string }
export interface CurrentSession { id: string; messages: DbMessage[] }
export interface Session {
  id: string;
  status: "active" | "archived";
  last_distilled_at: string | null;
  created_at: string;
  ended_at: string | null;
}
export interface SessionWithMessages extends Session {
  messages: DbMessage[];
  newSinceDistill?: number; // staleness cue (D8) — only on GET /sessions/:id
}
export interface KnowledgeItem {
  id: string; type: string; title: string; body_human: string; status: string;
}
export interface Recommendation {
  id: string; title: string; body: string; rationale: string;
  basis: string; status: string; confidence: string;
}

// ── Session / chat-list + provenance (UC15/UC16) ──────────────────────────────
export const listSessions = () => apiJson<Session[]>("/sessions");
/** Start a chat; `focusKnowledgeId` seeds it to evolve an existing item (UC17). */
export const newSession = (focusKnowledgeId?: string) =>
  apiJson<Session>("/sessions", {
    method: "POST",
    body: JSON.stringify(focusKnowledgeId ? { focusKnowledgeId } : {}),
  });
export const resumeSession = (id: string) =>
  apiJson<SessionWithMessages>(`/sessions/${id}/resume`, { method: "POST" });
export const getSessionWithMessages = (id: string) =>
  apiJson<SessionWithMessages>(`/sessions/${id}`);
export const sessionKnowledge = (id: string) =>
  apiJson<KnowledgeItem[]>(`/sessions/${id}/knowledge`);
export const sessionRecommendations = (id: string) =>
  apiJson<Recommendation[]>(`/sessions/${id}/recommendations`);
/** D8 — distil this chat + regenerate recommendations (repeatable, stamped). */
export const distillSession = (id: string) =>
  apiJson<{ count: number; recommendations: number }>(`/sessions/${id}/distill`, {
    method: "POST",
  });
export const archiveSession = (id: string) =>
  apiJson<Session>(`/sessions/${id}/archive`, { method: "POST" });
export const unarchiveSession = (id: string) =>
  apiJson<Session>(`/sessions/${id}/unarchive`, { method: "POST" });
